import { CheckCircle2, ListTodo, MessagesSquare, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AppVersion } from '../../components/AppVersion';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo, LogoMark } from '../../components/Logo';

const points = [
  { icon: MessagesSquare, text: 'Canales y mensajes directos en tiempo real' },
  { icon: ListTodo, text: 'Convierte cualquier mensaje en una tarea' },
  { icon: Zap, text: 'Rápido, sin ruido, pensado para equipos pequeños' },
];

/** Split layout: brand panel (desktop) + form. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-full bg-canvas lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:p-2">
      <aside className="relative isolate hidden overflow-hidden rounded-2xl bg-[oklch(0.11_0.02_268)] p-10 text-white lg:flex lg:flex-col">
        {/* The landing's night sky, so signing in feels like the same place. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="nx-stars absolute inset-0" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 overflow-hidden">
            <div className="nx-grid absolute inset-x-0 bottom-0 h-full" />
          </div>
          <div className="nx-aurora absolute inset-x-[-20%] bottom-[-30%] h-[75%]" />
        </div>
        <Link to="/" className="relative inline-flex items-center gap-2.5">
          <LogoMark className="size-9" />
          <span className="font-display text-xl font-semibold tracking-tight">nexo</span>
        </Link>
        <div className="relative mt-auto max-w-md">
          <h2 className="animate-rise font-display text-4xl leading-tight font-semibold tracking-tight text-balance">
            {t('Donde tu equipo habla y las cosas se hacen.')}
          </h2>
          <ul className="mt-8 flex flex-col gap-3.5">
            {points.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-[15px] text-white/85">
                <span className="flex size-8 items-center justify-center rounded-lg bg-white/8 text-glow ring-1 ring-white/15">
                  <Icon className="size-4" />
                </span>
                {t(text)}
              </li>
            ))}
          </ul>
          <div className="mt-10 rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur">
            <p className="text-[13px] text-white/70">{t('Esta semana en tu equipo')}</p>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <CheckCircle2 className="animate-pop size-4 text-emerald-300" />
              <span className="flex-1">{t('Nota de prensa del lanzamiento')}</span>
              <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[11px]">{t('Hecha')}</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15">
              <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-accent to-glow" />
            </div>
          </div>
        </div>
      </aside>

      <main className="relative flex flex-col items-center justify-center px-5 py-10">
        <LanguageSwitcher compact className="absolute top-3 right-3 text-muted" />
        <Link to="/" className="mb-10 lg:hidden">
          <Logo />
        </Link>
        <div className="animate-rise w-full max-w-[22rem]">
          <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1.5 mb-7 text-sm text-muted">{subtitle}</p>
          {children}
          <p className="mt-8 text-center text-sm text-muted">{footer}</p>
        </div>
        <AppVersion className="mt-10 text-xs text-subtle" />
      </main>
    </div>
  );
}
